import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import AddIcon from "@mui/icons-material/Add"
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createCategory,
  deleteCategory,
  getCategories,
  getCategoryById,
  updateCategory,
} from "../../services/categoryService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  name: "",
  description: "",
  is_active: true,
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.08)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.2,
          sm: 1.3,
        },

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",

        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform:
            "translateY(-1px)",

          boxShadow:
            "0 9px 24px rgba(15, 23, 42, 0.065)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.2,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.75,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function CategoriesPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    categories,
    setCategories,
  ] = useState([])

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)


  // =======================================================
  // DIALOG
  // =======================================================

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (previous) => ({
        ...previous,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD
  // =======================================================

  const loadCategories =
    async () => {
      try {
        setLoading(true)

        const data =
          await getCategories()

        setCategories(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(
          "Erreur chargement catégories :",
          error
        )

        showSnackbar(
          error?.response?.data?.detail ||
            "Impossible de charger les catégories.",
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadCategories()
  }, [])


  // =======================================================
  // RESET PAGE AFTER FILTER
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,
        page: 0,
      })
    )
  }, [
    search,
    statusFilter,
  ])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm = () => {
    setSelectedCategory(null)

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog = () => {
    resetForm()
    setOpen(true)
  }


  const openEditDialog =
    async (categoryId) => {
      try {
        const category =
          await getCategoryById(
            categoryId
          )

        setSelectedCategory(
          category
        )

        setFormData({
          name:
            category.name || "",

          description:
            category.description || "",

          is_active:
            typeof category.is_active ===
            "boolean"
              ? category.is_active
              : true,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response?.data?.detail ||
            "Impossible de charger la catégorie.",
          "error"
        )
      }
    }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target

    setFormData(
      (previous) => ({
        ...previous,

        [name]:
          name === "is_active"
            ? (
                value === "true" ||
                value === true
              )
            : value,
      })
    )
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !formData.name.trim()
      ) {
        showSnackbar(
          "Le nom de la catégorie est obligatoire.",
          "warning"
        )

        return
      }

      const payload = {
        name:
          formData.name.trim(),

        description:
          formData.description?.trim() ||
          null,

        is_active:
          Boolean(
            formData.is_active
          ),
      }

      try {
        setSaving(true)

        if (
          selectedCategory?.id
        ) {
          await updateCategory(
            selectedCategory.id,
            payload
          )

          showSnackbar(
            "Catégorie modifiée avec succès."
          )
        } else {
          await createCategory(
            payload
          )

          showSnackbar(
            "Catégorie créée avec succès."
          )
        }

        await loadCategories()

        setOpen(false)
        resetForm()
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response?.data?.detail ||
            "Impossible d'enregistrer la catégorie.",
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (category) => {
      const count =
        Number(
          category.product_count ||
            0
        )

      if (count > 0) {
        showSnackbar(
          `Cette catégorie contient ${count} produit(s). Désactivez-la au lieu de la supprimer.`,
          "warning"
        )

        return
      }

      const confirmed =
        window.confirm(
          `Supprimer définitivement la catégorie « ${category.name} » ?`
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteCategory(
          category.id
        )

        showSnackbar(
          "Catégorie supprimée avec succès."
        )

        await loadCategories()
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response?.data?.detail ||
            "Impossible de supprimer la catégorie.",
          "error"
        )
      }
    }


  // =======================================================
  // KPI
  // =======================================================

  const stats =
    useMemo(() => {
      return categories.reduce(
        (accumulator, item) => {
          accumulator.total += 1

          if (item.is_active) {
            accumulator.active += 1
          } else {
            accumulator.inactive += 1
          }

          accumulator.products +=
            Number(
              item.product_count ||
                0
            )

          return accumulator
        },
        {
          total: 0,
          active: 0,
          inactive: 0,
          products: 0,
        }
      )
    }, [
      categories,
    ])


  // =======================================================
  // FILTER
  // =======================================================

  const filteredCategories =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return categories.filter(
        (category) => {
          const matchesSearch =
            !keyword ||
            String(
              category.name || ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              category.description ||
                ""
            )
              .toLowerCase()
              .includes(keyword)

          const matchesStatus =
            statusFilter === "" ||
            String(
              category.is_active
            ) ===
              statusFilter

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      categories,
      search,
      statusFilter,
    ])


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedCategories =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredCategories.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredCategories,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredCategories.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,
        })
      )
    }
  }, [
    filteredCategories.length,
    paginationModel.page,
  ])


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "name",

          headerName:
            "Catégorie",

          minWidth: 190,

          flex: 1,

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                fontWeight={800}
                color="#0f172a"
                noWrap
                title={
                  params.value ||
                  ""
                }
              >
                {params.value ||
                  "-"}
              </Typography>
            ),
        },

        {
          field:
            "description",

          headerName:
            "Description",

          minWidth: 280,

          flex: 1.7,

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={
                  params.value ||
                  ""
                }
              >
                {params.value ||
                  "Aucune description"}
              </Typography>
            ),
        },

        {
          field:
            "product_count",

          headerName:
            "Produits",

          width: 105,

          align: "center",

          headerAlign:
            "center",

          renderCell:
            (params) => (
              <Chip
                size="small"

                label={
                  Number(
                    params.value ||
                      0
                  )
                }

                variant="outlined"

                color={
                  Number(
                    params.value ||
                      0
                  ) > 0
                    ? "primary"
                    : "default"
                }

                sx={{
                  fontWeight: 800,

                  minWidth: 38,
                }}
              />
            ),
        },

        {
          field:
            "is_active",

          headerName:
            "Statut",

          width: 105,

          renderCell:
            (params) => (
              <Chip
                size="small"

                label={
                  params.value
                    ? "Active"
                    : "Inactive"
                }

                color={
                  params.value
                    ? "success"
                    : "default"
                }

                variant={
                  params.value
                    ? "filled"
                    : "outlined"
                }

                sx={{
                  fontWeight: 800,
                }}
              />
            ),
        },

        {
          field:
            "actions",

          headerName:
            "Actions",

          width: 195,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"
                spacing={0.7}
              >
                <Button
                  size="small"

                  variant="contained"

                  disableElevation

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 80,
                  }}
                >
                  Modifier
                </Button>

                <Button
                  size="small"

                  variant="outlined"

                  color="error"

                  onClick={() =>
                    handleDelete(
                      params.row
                    )
                  }

                  sx={{
                    minWidth: 90,
                  }}
                >
                  Supprimer
                </Button>
              </Stack>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize:
        ROWS_PER_PAGE,
    })
  }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius: 2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            gap: 1.5,

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Catégories produits
            </Typography>

            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              Classement général du catalogue,
              indépendant de la nature et du processus
              métier du produit.
            </Typography>
          </Box>


          <Button
            variant="contained"

            disableElevation

            startIcon={
              <AddIcon />
            }

            onClick={
              openAddDialog
            }

            sx={{
              minHeight: 40,

              px: 2.2,

              minWidth: {
                xs: "100%",
                sm: 180,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            Nouvelle catégorie
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,

            alignItems: "center",

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          Une catégorie sert uniquement à classer les
          articles : « Textile », « Boissons »,
          « Matelas », « Pièces détachées », etc.
          La nature du produit est définie séparément
          dans sa fiche Produit.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                md:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Catégories"

            value={
              stats.total
            }

            caption="Total du catalogue"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <CategoryRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Actives"

            value={
              stats.active
            }

            caption="Disponibles actuellement"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Inactives"

            value={
              stats.inactive
            }

            caption="Non utilisées"

            accent="#64748b"

            background="rgba(100, 116, 139, 0.10)"

            icon={
              <PauseCircleOutlineRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Produits classés"

            value={
              stats.products
            }

            caption="Articles rattachés"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",

                  sm:
                    "minmax(0, 1fr) minmax(150px, 0.35fr) auto",
                },

              gap: 1,

              alignItems:
                "center",
            }}
          >
            <TextField
              fullWidth

              size="small"

              label="Rechercher"

              placeholder="Nom ou description..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select

              fullWidth

              size="small"

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="true">
                Active
              </MenuItem>

              <MenuItem value="false">
                Inactive
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  sm: 115,
                },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            CATEGORY REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            minWidth: 0,

            overflow: "hidden",

            borderRadius: 3.5,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* TABLE HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des catégories
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Catégories utilisées pour classer
                les produits du catalogue
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredCategories.length} catégorie${
                filteredCategories.length >
                1
                  ? "s"
                  : ""
              }`}

              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflow: "hidden",

                bgcolor: "#ffffff",
              }}
            >
              <DataGrid
                autoHeight

                rows={
                  filteredCategories
                }

                columns={
                  columns
                }

                loading={
                  loading
                }

                disableRowSelectionOnClick

                disableColumnSelector

                pagination

                paginationModel={
                  paginationModel
                }

                onPaginationModelChange={
                  setPaginationModel
                }

                pageSizeOptions={[
                  ROWS_PER_PAGE,
                ]}

                rowHeight={58}

                columnHeaderHeight={48}

                localeText={{
                  noRowsLabel:
                    "Aucune catégorie enregistrée",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}

                sx={{
                  border: 0,

                  width: "100%",

                  minWidth: 0,

                  color: "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor: "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor: "#f8fafc",

                      px: 1.15,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight: 900,

                      color: "#334155",

                      fontSize:
                        "0.79rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      px: 1.15,

                      minWidth: 0,

                      display: "flex",

                      alignItems:
                        "center",

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight: 50,

                      bgcolor:
                        "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des catégories...
                  </Typography>
                </Box>
              ) : paginatedCategories.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune catégorie trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedCategories.map(
                    (category) => (
                      <Paper
                        key={
                          category.id
                        }

                        variant="outlined"

                        sx={{
                          p: {
                            xs: 1.4,
                            sm: 1.6,
                          },

                          borderRadius: 3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={1.3}
                        >

                          {/* TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                                sx={{
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {category.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Catégorie #{category.id}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              label={
                                category.is_active
                                  ? "Active"
                                  : "Inactive"
                              }

                              color={
                                category.is_active
                                  ? "success"
                                  : "default"
                              }

                              variant={
                                category.is_active
                                  ? "filled"
                                  : "outlined"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* DESCRIPTION */}

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Description
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.2,

                                lineHeight:
                                  1.5,

                                overflowWrap:
                                  "anywhere",
                              }}
                            >
                              {category.description ||
                                "Aucune description"}
                            </Typography>
                          </Box>


                          {/* PRODUCTS */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              alignItems:
                                "center",

                              gap: 0.8,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Produits classés
                            </Typography>

                            <Chip
                              size="small"

                              label={
                                Number(
                                  category.product_count ||
                                    0
                                )
                              }

                              color={
                                Number(
                                  category.product_count ||
                                    0
                                ) > 0
                                  ? "primary"
                                  : "default"
                              }

                              variant="outlined"

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.8,
                            }}
                          >
                            <Button
                              size="small"

                              variant="contained"

                              disableElevation

                              onClick={() =>
                                openEditDialog(
                                  category.id
                                )
                              }
                            >
                              Modifier
                            </Button>

                            <Button
                              size="small"

                              variant="outlined"

                              color="error"

                              onClick={() =>
                                handleDelete(
                                  category
                                )
                              }
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  filteredCategories.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          CATEGORY DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.4,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedCategory
              ? "Modifier la catégorie"
              : "Nouvelle catégorie"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedCategory
              ? "Modifiez les informations de cette catégorie du catalogue."
              : "Créez une catégorie pour classer les produits du catalogue."}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >
            {selectedCategory &&
            Number(
              selectedCategory.product_count ||
                0
            ) > 0 ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 3,
                }}
              >
                Cette catégorie contient déjà{" "}
                <strong>
                  {Number(
                    selectedCategory.product_count ||
                      0
                  )}
                </strong>{" "}
                produit(s).
              </Alert>
            ) : null}


            <TextField
              size="small"

              label="Nom de la catégorie"

              name="name"

              value={
                formData.name
              }

              onChange={
                handleChange
              }

              required

              fullWidth

              disabled={
                saving
              }

              placeholder="Ex. Matelas, Boissons, Textile..."
            />


            <TextField
              label="Description"

              name="description"

              value={
                formData.description
              }

              onChange={
                handleChange
              }

              multiline

              minRows={3}

              fullWidth

              disabled={
                saving
              }

              placeholder="Décrivez brièvement les produits classés dans cette catégorie..."
            />


            <TextField
              select

              size="small"

              label="Statut"

              name="is_active"

              value={
                String(
                  formData.is_active
                )
              }

              onChange={
                handleChange
              }

              fullWidth

              disabled={
                saving
              }
            >
              <MenuItem value="true">
                Active
              </MenuItem>

              <MenuItem value="false">
                Inactive
              </MenuItem>
            </TextField>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"

            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedCategory
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          4000
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          variant="filled"

          onClose={
            closeSnackbar
          }

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default CategoriesPage